import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, FileText, Receipt } from 'lucide-react';
import { companyApi, licenseApi, expenseApi, personApi, Company, License, Expense, Person } from '../services/api.ts';

const ONE_DAY_MS = 86400000;

const getPstDateString = () => {
  const now = new Date();
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Los_Angeles',
    year: 'numeric', month: '2-digit', day: '2-digit',
  });
  const parts = fmt.formatToParts(now);
  const y = parts.find(p => p.type === 'year')?.value;
  const m = parts.find(p => p.type === 'month')?.value;
  const d = parts.find(p => p.type === 'day')?.value;
  return `${y}-${m}-${d}`;
};

const getDaysRemaining = (expirationDate: string | undefined | null): number => {
  if (!expirationDate || expirationDate.trim() === '') return 999;
  const todayPst = getPstDateString();
  const todayParts = todayPst.split('-').map(Number);
  const expireStr = expirationDate.split('T')[0].slice(0, 10);
  const expireParts = expireStr.split('-').map(Number);
  if (expireParts.some(isNaN)) return 999;
  const todayUtcMs = Date.UTC(todayParts[0], todayParts[1] - 1, todayParts[2]);
  const expireUtcMs = Date.UTC(expireParts[0], expireParts[1] - 1, expireParts[2]);
  return Math.round((expireUtcMs - todayUtcMs) / ONE_DAY_MS);
};

const norm = (v: unknown): string => (v === null || v === undefined || String(v).trim() === '' ? '' : String(v).trim());

// Expense 狀態分類
const expenseCategory = (status: string): 'pendingApproval' | 'pendingPayment' | 'paid' | 'other' => {
  const s = status.toLowerCase();
  if (s === 'paid') return 'paid';
  if (s.includes('payment') || s.includes('支付') || s.includes('支票') || s.includes('processing') || s.includes('開票')) return 'pendingPayment';
  if (s.includes('approv') || s.includes('審批') || s.includes('pending')) return 'pendingApproval';
  return 'other';
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [licenses, setLicenses] = useState<License[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [persons, setPersons] = useState<Person[]>([]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [co, lic, exp, per] = await Promise.all([
          companyApi.getAll().then(r => Array.isArray(r.data) ? r.data : (r.data?.list || [])),
          licenseApi.getAll().then(r => Array.isArray(r.data) ? r.data : (r.data?.list || [])),
          expenseApi.getAll().then(r => Array.isArray(r.data) ? r.data : (r.data?.list || [])),
          personApi.getAll().then(r => (r.data as any)?.data || (Array.isArray(r.data) ? r.data : [])),
        ]);
        setCompanies(co);
        setLicenses(lic);
        setExpenses(exp);
        setPersons(per);
      } catch (e) {
        console.error('Dashboard load failed:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const stats = useMemo(() => {
    // Company
    const activeCompanies = companies.filter(c => !c.deleted_at && (c.status || '').toLowerCase() === 'active');
    const inactiveCompanies = companies.filter(c => !c.deleted_at && (c.status || '').toLowerCase() !== 'active');
    const groupMap = new Map<string, number>();
    for (const c of activeCompanies) {
      const g = norm(c.group_name) || '(No Group)';
      groupMap.set(g, (groupMap.get(g) || 0) + 1);
    }
    const byGroup = Array.from(groupMap.entries()).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);

    // License
    const validLicenses = licenses.filter(l => !l.deleted_at);
    const activeLicenses = validLicenses.filter(l => {
      const s = (l.status || '').toLowerCase();
      return s !== 'cancelled' && s !== 'terminated' && s !== 'deleted';
    });
    const expiredLicenses = validLicenses.filter(l => (l.status || '').toLowerCase() === 'cancelled');

    const expiring = { overdue: 0, d30: 0, d60: 0, d90: 0 };
    const expiringSoonList: License[] = [];
    for (const l of activeLicenses) {
      const d = getDaysRemaining(l.expiration_date);
      if (d === 999) continue;
      if (d < 0) { expiring.overdue++; expiringSoonList.push(l); }
      else if (d <= 30) { expiring.d30++; expiringSoonList.push(l); }
      else if (d <= 60) { expiring.d60++; expiringSoonList.push(l); }
      else if (d <= 90) { expiring.d90++; expiringSoonList.push(l); }
    }
    expiringSoonList.sort((a, b) => getDaysRemaining(a.expiration_date) - getDaysRemaining(b.expiration_date));

    const stateMap = new Map<string, number>();
    const catMap = new Map<string, number>();
    for (const l of activeLicenses) {
      const s = norm(l.state);
      if (s) stateMap.set(s, (stateMap.get(s) || 0) + 1);
      for (const c of (l.category || '').split(',').map(x => x.trim()).filter(Boolean)) {
        catMap.set(c, (catMap.get(c) || 0) + 1);
      }
    }
    const byState = Array.from(stateMap.entries()).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 6);
    const byCategory = Array.from(catMap.entries()).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 6);

    // Expense
    let pendingApproval = 0, pendingPayment = 0, paid = 0, totalExpense = 0;
    const pendingApprovalList: Expense[] = [];
    const pendingPaymentList: Expense[] = [];
    for (const e of expenses) {
      const cat = expenseCategory(norm(e.status));
      if (cat === 'pendingApproval') { pendingApproval++; pendingApprovalList.push(e); }
      else if (cat === 'pendingPayment') { pendingPayment++; pendingPaymentList.push(e); }
      else if (cat === 'paid') { paid++; }
      totalExpense += Number(e.subtotal) || 0;
    }

    return {
      companies: { total: companies.length, active: activeCompanies.length, inactive: inactiveCompanies.length, byGroup },
      licenses: { total: validLicenses.length, active: activeLicenses.length, expired: expiredLicenses.length, expiring, byState, byCategory, expiringSoonList: expiringSoonList.slice(0, 6) },
      expenses: { pendingApproval, pendingPayment, paid, total: totalExpense, pendingApprovalList, pendingPaymentList },
      persons: persons.length,
    };
  }, [companies, licenses, expenses, persons]);

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="text-slate-500 text-sm">Loading...</div></div>;
  }

  const groupCount = stats.companies.byGroup.length;

  return (
    <div className="min-h-[80vh] flex items-center">
      <div className="w-full space-y-6">
      {/* 三個板塊指標卡 — 統一結構：大數字 + 三個小統計 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Company */}
        <button onClick={() => navigate('/company')} className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm flex flex-col text-left hover:shadow-md hover:border-blue-300 transition-all">
          <div className="flex items-center gap-2 mb-5">
            <div className="bg-blue-500 p-2 rounded-lg"><Building2 className="w-4 h-4 text-white" /></div>
            <h3 className="text-base font-semibold text-slate-800">Company</h3>
          </div>
          <div className="text-center mb-5">
            <div className="text-4xl font-bold text-slate-800 tabular-nums">{stats.companies.total}</div>
            <div className="text-xs text-slate-500 mt-1">Total Companies</div>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center mt-auto">
            <div><div className="text-lg font-bold text-slate-700 tabular-nums">{groupCount}</div><div className="text-[11px] text-slate-500">Groups</div></div>
            <div><div className="text-lg font-bold text-emerald-600 tabular-nums">{stats.companies.active}</div><div className="text-[11px] text-slate-500">Active</div></div>
            <div><div className="text-lg font-bold text-slate-400 tabular-nums">{stats.companies.inactive}</div><div className="text-[11px] text-slate-500">Inactive</div></div>
          </div>
        </button>

        {/* License */}
        <button onClick={() => navigate('/license')} className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm flex flex-col text-left hover:shadow-md hover:border-indigo-300 transition-all">
          <div className="flex items-center gap-2 mb-5">
            <div className="bg-indigo-500 p-2 rounded-lg"><FileText className="w-4 h-4 text-white" /></div>
            <h3 className="text-base font-semibold text-slate-800">License</h3>
          </div>
          <div className="text-center mb-5">
            <div className="text-4xl font-bold text-slate-800 tabular-nums">{stats.licenses.total}</div>
            <div className="text-xs text-slate-500 mt-1">Total Licenses</div>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center mt-auto">
            <div><div className="text-lg font-bold text-emerald-600 tabular-nums">{stats.licenses.active}</div><div className="text-[11px] text-slate-500">Active</div></div>
            <div><div className="text-lg font-bold text-slate-400 tabular-nums">{stats.licenses.expired}</div><div className="text-[11px] text-slate-500">Expired</div></div>
            <div><div className="text-lg font-bold text-red-500 tabular-nums">{stats.licenses.expiring.overdue}</div><div className="text-[11px] text-slate-500">Overdue</div></div>
          </div>
        </button>

        {/* Expense */}
        <button onClick={() => navigate('/expense')} className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm flex flex-col text-left hover:shadow-md hover:border-emerald-300 transition-all">
          <div className="flex items-center gap-2 mb-5">
            <div className="bg-emerald-500 p-2 rounded-lg"><Receipt className="w-4 h-4 text-white" /></div>
            <h3 className="text-base font-semibold text-slate-800">Expense</h3>
          </div>
          <div className="text-center mb-5">
            <div className="text-4xl font-bold text-slate-800 tabular-nums">${stats.expenses.total.toLocaleString()}</div>
            <div className="text-xs text-slate-500 mt-1">Period Total</div>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center mt-auto">
            <div><div className="text-lg font-bold text-amber-600 tabular-nums">{stats.expenses.pendingApproval}</div><div className="text-[11px] text-slate-500">Approval</div></div>
            <div><div className="text-lg font-bold text-sky-600 tabular-nums">{stats.expenses.pendingPayment}</div><div className="text-[11px] text-slate-500">Payment</div></div>
            <div><div className="text-lg font-bold text-emerald-600 tabular-nums">{stats.expenses.paid}</div><div className="text-[11px] text-slate-500">Paid</div></div>
          </div>
        </button>
      </div>
      </div>
    </div>
  );
}
